import { useEffect, useMemo, useState } from 'react'
import { collection, doc, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { scopedReadConstraints } from '../services/accessScopeService'
import type { MeetingAgendaNode, MeetingRecord } from '../types/meetings'

export type MeetingReadinessItem = {
  key: string
  nodeId: string
  nodeType: MeetingAgendaNode['nodeType']
  title: string
  ready: boolean
  score: number
  missing: string[]
}

export type MeetingReadinessState = {
  score: number
  ready: number
  total: number
  items: MeetingReadinessItem[]
  missing: string[]
}

const emptyState: MeetingReadinessState = { score: 0, ready: 0, total: 0, items: [], missing: [] }
const asNumber = (value: unknown, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback
const asString = (value: unknown, fallback = '') => typeof value === 'string' && value.trim() ? value.trim() : fallback

export const useMeetingReadiness = (meeting: MeetingRecord | null, threshold = 80): MeetingReadinessState => {
  const meetingKey = meeting ? `${meeting.id}:${threshold}` : ''
  const [recordsState, setRecordsState] = useState<{
    key: string
    records: Record<string, MeetingReadinessItem>
  }>({ key: '', records: {} })

  useEffect(() => {
    if (!meeting?.workspaceId || !meeting.agendaNodes?.length) return undefined
    const currentMeetingKey = `${meeting.id}:${threshold}`
    const stops: Unsubscribe[] = []
    meeting.agendaNodes.forEach((agendaNode) => {
      const key = `${agendaNode.nodeType}:${agendaNode.nodeId}`
      if (agendaNode.nodeType === 'CHECKLIST') {
        stops.push(onSnapshot(doc(firestoreDb, 'checklists', agendaNode.nodeId), (snapshot) => {
          const data = snapshot.data() ?? {}
          const ready = data.workflowStatus === 'COMPLETED' || data.isCompleted === true || data.isReady === true
          setRecordsState((current) => {
            const base = current.key === currentMeetingKey ? current.records : {}
            return {
              key: currentMeetingKey,
              records: {
                ...base,
                [key]: {
                  key,
                  nodeId: agendaNode.nodeId,
                  nodeType: agendaNode.nodeType,
                  title: asString(data.title, agendaNode.title),
                  ready,
                  score: ready ? 100 : 0,
                  missing: ready ? [] : [asString(data.title, agendaNode.title)],
                },
              },
            }
          })
        }))
        return
      }

      stops.push(onSnapshot(doc(firestoreDb, 'elements', agendaNode.nodeId), (snapshot) => {
        const data = snapshot.data() ?? {}
        const score = asNumber(data.readinessScore)
        const ready = score >= threshold
        setRecordsState((current) => {
          const base = current.key === currentMeetingKey ? current.records : {}
          return {
            key: currentMeetingKey,
            records: {
              ...base,
              [key]: {
                key,
                nodeId: agendaNode.nodeId,
                nodeType: agendaNode.nodeType,
                title: asString(data.title, agendaNode.title),
                ready,
                score,
                missing: ready ? [] : [asString(data.title, agendaNode.title)],
              },
            },
          }
        })
      }))
      stops.push(onSnapshot(query(
        collection(firestoreDb, 'checklists'),
        where('workspaceId', '==', meeting.workspaceId),
        where('parentContainerId', '==', agendaNode.nodeId),
        ...scopedReadConstraints(meeting.workspaceId),
      ), (snapshot) => {
        const total = snapshot.size
        const readyCount = snapshot.docs.filter((entry) => entry.data().workflowStatus === 'COMPLETED' || entry.data().isCompleted === true || entry.data().isReady === true).length
        const score = total ? Math.round((readyCount / total) * 100) : 0
        const missing = snapshot.docs
          .filter((entry) => !(entry.data().workflowStatus === 'COMPLETED' || entry.data().isCompleted === true || entry.data().isReady === true))
          .map((entry) => asString(entry.data().title, 'Checklist item'))
        setRecordsState((current) => {
          const base = current.key === currentMeetingKey ? current.records : {}
          const previous = base[key]
          const combinedScore = Math.max(score, previous?.score ?? 0)
          const ready = combinedScore >= threshold
          return {
            key: currentMeetingKey,
            records: {
              ...base,
              [key]: {
                key,
                nodeId: agendaNode.nodeId,
                nodeType: agendaNode.nodeType,
                title: previous?.title ?? agendaNode.title,
                ready,
                score: combinedScore,
                missing: ready ? [] : missing.length ? missing : [agendaNode.title],
              },
            },
          }
        })
      }))
    })
    return () => stops.forEach((stop) => stop())
  }, [meeting, threshold])

  const agendaNodes = meeting?.agendaNodes

  return useMemo(() => {
    if (!agendaNodes?.length) return emptyState
    const activeRecords = recordsState.key === meetingKey ? recordsState.records : {}
    const items = agendaNodes.map((node) => activeRecords[`${node.nodeType}:${node.nodeId}`] ?? {
      key: `${node.nodeType}:${node.nodeId}`,
      nodeId: node.nodeId,
      nodeType: node.nodeType,
      title: node.title,
      ready: false,
      score: 0,
      missing: [node.title],
    })
    const ready = items.filter((item) => item.ready).length
    return {
      score: items.length ? Math.round((ready / items.length) * 100) : 0,
      ready,
      total: items.length,
      items,
      missing: items.flatMap((item) => item.missing),
    }
  }, [agendaNodes, meetingKey, recordsState])
}
